import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, loginSuccess } from '../slices/authSlice';
import { clearAuthToken } from '../utils/authToken';
import RushEditor from './RushPages/Editor';

/**
 * Guest shell.
 *
 * The guest sees the existing Editor (which already has its own chrome) so
 * the experience feels familiar. On top of that we float two small things:
 *
 *   1. A "Guest review · X days left" pill, so they know what kind of
 *      access they have and when it ends.
 *   2. A tiny avatar menu with Sign out (no Settings, no workspace switcher,
 *      no notifications — none of that applies to a guest).
 *
 * Stage 4c locks the Editor's write actions when accountType === 'guest', so
 * we don't need to hide anything from out here.
 */
export default function GuestApp() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useSelector((s) => s.auth.user) || {};
    const token = useSelector((s) => s.auth.token);
    const [menuOpen, setMenuOpen] = useState(false);
    const [nameEdit, setNameEdit] = useState(false);
    const [nameDraft, setNameDraft] = useState('');
    const [savingName, setSavingName] = useState(false);
    const menuRef = useRef(null);

    const saveName = async () => {
        const next = nameDraft.trim();
        if (!next || savingName) return;
        setSavingName(true);
        try {
            await axios.put('/api/users/me',
                { firstName: next },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            // Refresh /me so Redux carries the new firstName (used by comment
            // signatures and the avatar initials).
            const me = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            dispatch(loginSuccess({ token, user: me.data }));
            setNameEdit(false);
            setMenuOpen(false);
        } catch (_) { /* keep dialog open on failure */ }
        setSavingName(false);
    };

    useEffect(() => {
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        if (menuOpen) document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    const signOut = () => {
        clearAuthToken();
        dispatch(logout());
        navigate('/login', { replace: true });
    };

    const expiresAt = user.guestExpiresAt ? new Date(user.guestExpiresAt) : null;
    const daysLeft = expiresAt
        ? Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
        : null;
    const expiryLabel = daysLeft == null
        ? 'View & comment'
        : daysLeft === 0
            ? 'Expires today'
            : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;

    const initials = ((user.firstName || user.email || 'G')[0] || 'G').toUpperCase();

    return (
        <>
            {/* The Editor is the page. We render it underneath the floating
                pill so it owns layout end-to-end. */}
            <RushEditor />

            {/* Floating guest context — top-right of the viewport. Pointer
                events are isolated so the Editor's own top bar can be clicked
                where this overlay doesn't sit. */}
            <div
                style={{
                    position: 'fixed', top: 12, right: 16, zIndex: 9999,
                    display: 'flex', alignItems: 'center', gap: 10,
                    pointerEvents: 'none',
                }}
            >
                <span
                    style={{
                        pointerEvents: 'auto',
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        fontSize: 11, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        background: '#fbe8de', color: '#b85a3e',
                        padding: '5px 10px', borderRadius: 999,
                        border: '1px solid #f0c8b4',
                        boxShadow: '0 2px 8px rgba(35,18,8,0.06)',
                    }}
                    title="You're reviewing this project as a guest. Access is read-only with comments enabled."
                >
                    Guest · {expiryLabel}
                </span>

                <div ref={menuRef} style={{ position: 'relative', pointerEvents: 'auto' }}>
                    <button
                        type="button"
                        onClick={() => setMenuOpen((v) => !v)}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                        style={{
                            width: 32, height: 32, borderRadius: '50%',
                            border: '1.5px solid #ece8df', background: '#fbfaf7',
                            color: '#0e0e10', fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontFamily: 'inherit',
                            boxShadow: '0 2px 8px rgba(35,18,8,0.06)',
                        }}
                        title={user.email || 'Guest'}
                    >
                        {initials}
                    </button>
                    {menuOpen && (
                        <div
                            role="menu"
                            style={{
                                position: 'absolute', top: '110%', right: 0,
                                minWidth: 220, background: '#fff',
                                border: '1px solid #ece8df', borderRadius: 12,
                                boxShadow: '0 10px 30px rgba(35,18,8,0.12)',
                                padding: 6, fontSize: 13,
                            }}
                        >
                            <div style={{ padding: '8px 10px 6px' }}>
                                <div style={{ fontWeight: 600, color: '#0e0e10' }}>
                                    {user.firstName || user.email || 'Guest reviewer'}
                                </div>
                                <div style={{ fontSize: 12, color: '#6c6a64', marginTop: 2 }}>
                                    {user.email}
                                </div>
                            </div>
                            <div style={{ height: 1, background: '#ece8df', margin: '4px 0' }} />
                            <button
                                type="button"
                                role="menuitem"
                                onClick={() => { setNameDraft(user.firstName || ''); setNameEdit(true); setMenuOpen(false); }}
                                style={{
                                    width: '100%', textAlign: 'left',
                                    background: 'transparent', border: 0,
                                    padding: '8px 10px', borderRadius: 8,
                                    fontFamily: 'inherit', fontSize: 13,
                                    color: '#0e0e10', cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#fbfaf7'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                Edit name
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                onClick={signOut}
                                style={{
                                    width: '100%', textAlign: 'left',
                                    background: 'transparent', border: 0,
                                    padding: '8px 10px', borderRadius: 8,
                                    fontFamily: 'inherit', fontSize: 13,
                                    color: '#0e0e10', cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#fbfaf7'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                            >
                                Sign out
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {nameEdit && (
                <div
                    onMouseDown={(e) => { if (e.target === e.currentTarget) setNameEdit(false); }}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(20,12,4,0.32)',
                        zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <div style={{
                        width: 360, background: '#fff', borderRadius: 14,
                        boxShadow: '0 30px 80px rgba(35,18,8,0.18)', overflow: 'hidden',
                    }}>
                        <div style={{ padding: '18px 20px 8px' }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#0e0e10' }}>Edit your name</div>
                            <div style={{ fontSize: 12.5, color: '#6c6a64', marginTop: 4 }}>
                                Shown on the comments you leave.
                            </div>
                        </div>
                        <form
                            onSubmit={(e) => { e.preventDefault(); saveName(); }}
                            style={{ padding: '6px 20px 18px' }}
                        >
                            <input
                                type="text"
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                placeholder="Your name"
                                autoFocus
                                style={{
                                    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                                    border: '1.5px solid #dcd6c7', borderRadius: 10,
                                    fontFamily: 'inherit', fontSize: 13.5, color: '#0e0e10',
                                    outline: 'none',
                                }}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                                <button
                                    type="button"
                                    onClick={() => setNameEdit(false)}
                                    style={{
                                        border: 0, background: 'transparent', color: '#0e0e10',
                                        fontSize: 13, fontFamily: 'inherit', padding: '8px 12px',
                                        borderRadius: 8, cursor: 'pointer',
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={!nameDraft.trim() || savingName}
                                    style={{
                                        border: 0, background: '#d97757', color: '#fff',
                                        fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                                        padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                                        opacity: (!nameDraft.trim() || savingName) ? 0.6 : 1,
                                    }}
                                >
                                    {savingName ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
